/**
 * Demo fixtures for the Palletways Orders wallboard (orders_daily UI).
 * Relative to Europe/London "today" so Today / Yesterday / Last 7 Days chips work.
 */

import { addUtcDays, ratioPercent } from '../lib/calc';
import type {
  CollectionStage,
  OrdersDailyRow,
  OrdersDailyStatusBucket,
  OrdersDailySummary,
  OperationalMetric,
  OperationalProgress,
} from '../types/ordersDailyTruth';
import { summariseEtaFromDeliveries } from '../utils/ordersDailyEtaSummary';
import { londonDateString } from '../utils/ordersDailyPresentation';

function measure(rows: readonly OrdersDailyRow[]): OperationalMetric {
  const consignments = new Set(rows.map((r) => r.tracking_id));
  const lifts = rows.reduce((sum, r) => sum + (r.lifts ?? 0), 0);
  return { consignments: consignments.size, lifts };
}

function progress(
  rows: readonly OrdersDailyRow[],
  isComplete: (r: OrdersDailyRow) => boolean,
  isPartial?: (r: OrdersDailyRow) => boolean,
): OperationalProgress {
  const total = measure(rows);
  const completed = measure(rows.filter(isComplete));
  const open = measure(rows.filter((r) => r.open));
  const partial = isPartial ? measure(rows.filter(isPartial)) : undefined;
  return {
    total,
    completed,
    open,
    ...(partial ? { partial } : {}),
    completion_pct: ratioPercent(completed.consignments, total.consignments),
    target: { consignments: null, lifts: null },
  };
}

function bucketMetric(rows: readonly OrdersDailyRow[], bucket: OrdersDailyStatusBucket): OperationalMetric {
  return measure(rows.filter((r) => r.status_bucket === bucket));
}

function baseRow(
  partial: Partial<OrdersDailyRow> & Pick<OrdersDailyRow, 'tracking_id' | 'direction' | 'grain_date'>,
): OrdersDailyRow {
  return {
    organisation_id: 'demo',
    lifts: 2,
    missed_am: false,
    at_risk_1700: false,
    booked_in_excluded: false,
    status_bucket: 'unmapped',
    collected: false,
    delivered: false,
    open: true,
    unmapped_status_code: false,
    operational_date_source: partial.direction === 'collection' ? 'date_entered' : 'due_date',
    operational_date_semantics: partial.direction === 'collection' ? 'provisional' : 'confirmed',
    source_event_ids: ['demo-event'],
    raw_payload_ref: 'demo://orders-daily',
    computed_at: new Date().toISOString(),
    data_origin: 'computed_from_events',
    consignment_no: partial.tracking_id.slice(-6),
    customer: 'Demo Customer Ltd',
    type: 'AMB',
    col_depot: '120',
    del_depot: '340',
    service_code: 'ND',
    service_name: 'Next Day',
    ...partial,
  };
}

/** Build a pool of demo legs across the last 7 London days (+ a few older for overdue). */
export function buildDemoOrdersDailyRows(asOf: Date = new Date()): OrdersDailyRow[] {
  const today = londonDateString(asOf);
  const y = (offset: number) => addUtcDays(today, offset);
  const rows: OrdersDailyRow[] = [];

  const pushDelivery = (
    id: string,
    dayOffset: number,
    opts: {
      bucket: OrdersDailyStatusBucket;
      delivered?: boolean;
      open?: boolean;
      lifts?: number;
      customer?: string;
      partial?: boolean;
      liftsDelivered?: number;
      liftsOutstanding?: number;
      everDelivered?: boolean;
      failedAttempts?: number;
      missedAm?: boolean;
      atRisk?: boolean;
      impact?: number;
      /** Explicit PDT on-time result for delivered demo legs. */
      etaOnTime?: boolean;
    },
  ) => {
    const grain = y(dayOffset);
    const delivered = opts.delivered ?? opts.bucket === 'delivered';
    const open = opts.open ?? !delivered;
    rows.push(baseRow({
      tracking_id: id,
      direction: 'delivery',
      grain_date: grain,
      due_date: grain,
      status_bucket: opts.bucket,
      delivered,
      open,
      lifts: opts.lifts ?? 3,
      customer: opts.customer ?? `Demo Customer ${id.slice(-3)}`,
      consignment_no: `C${id.slice(-6)}`,
      partial_delivery: opts.partial,
      lifts_delivered: opts.liftsDelivered,
      lifts_outstanding: opts.liftsOutstanding,
      ever_delivered: opts.everDelivered,
      failed_attempts: opts.failedAttempts,
      missed_am: opts.missedAm ?? false,
      at_risk_1700: opts.atRisk ?? false,
      impact_gbp: opts.impact,
      pod_date: delivered ? grain : undefined,
      pod_time: delivered ? '14:22' : undefined,
      ...(delivered
        ? {
            eta_at: `${grain}T14:34:00`,
            eta_window_start: `${grain}T13:34:00`,
            eta_window_end: `${grain}T15:34:00`,
            eta_basis: 'window_end' as const,
            eta_on_time: opts.etaOnTime ?? true,
          }
        : {}),
    }));
  };

  const pushCollection = (
    id: string,
    dayOffset: number,
    opts: { collected?: boolean; lifts?: number; customer?: string },
  ) => {
    const grain = y(dayOffset);
    const collected = opts.collected ?? false;
    rows.push(baseRow({
      tracking_id: id,
      direction: 'collection',
      grain_date: grain,
      collection_date: grain,
      status_bucket: collected ? 'delivered' : 'unmapped',
      collection_stage: collected ? 'dispatched_onward' : 'awaiting_collection',
      collection_stage_provenance: collected ? 'Observed' : 'Unknown',
      collection_poc_recorded: collected,
      collection_condition: 'on_track',
      collection_source: Number(id.slice(-1)) % 3 === 0 ? 'network' : 'own_customer',
      collected,
      open: !collected,
      lifts: opts.lifts ?? 2,
      customer: opts.customer ?? `Collection Co ${id.slice(-2)}`,
      consignment_no: `K${id.slice(-6)}`,
    }));
  };

  // Today — strong completion picture (matches wallboard screenshot vibe)
  for (let i = 0; i < 170; i++) {
    pushDelivery(`6005010${String(1000 + i).padStart(4, '0')}`, 0, {
      bucket: 'delivered',
      delivered: true,
      lifts: 1 + (i % 4),
      // ~1 in 8 late so the ETA ring is not stuck at 100%.
      etaOnTime: i % 8 !== 0,
    });
  }
  pushDelivery('60050102608', 0, {
    bucket: 'at_depot',
    delivered: false,
    open: true,
    everDelivered: true,
    customer: 'Lineout (UK) Ltd',
    lifts: 4,
  });
  pushDelivery('60050102609', 0, {
    bucket: 'partial',
    delivered: false,
    open: true,
    partial: true,
    lifts: 13,
    liftsDelivered: 9,
    liftsOutstanding: 4,
    customer: 'Partial Pallet Co',
  });
  pushDelivery('60050102610', 0, {
    bucket: 'out_for_delivery',
    delivered: false,
    lifts: 2,
  });
  pushDelivery('60050102611', 0, {
    bucket: 'exception',
    delivered: false,
    failedAttempts: 3,
    customer: 'Bounce Back Ltd',
    impact: 45,
  });
  pushDelivery('60050102612', -1, {
    bucket: 'at_depot',
    delivered: false,
    missedAm: true,
    impact: 35,
    customer: 'AM Missed Haulage',
  });

  for (let i = 0; i < 58; i++) {
    pushCollection(`7005010${String(2000 + i).padStart(4, '0')}`, 0, { collected: true });
  }
  for (let i = 0; i < 3; i++) {
    pushCollection(`7005010${String(2100 + i).padStart(4, '0')}`, 0, { collected: false });
  }

  // Yesterday
  for (let i = 0; i < 160; i++) {
    pushDelivery(`6005020${String(1000 + i).padStart(4, '0')}`, -1, {
      bucket: 'delivered',
      delivered: true,
    });
  }
  for (let i = 0; i < 8; i++) {
    pushDelivery(`6005020${String(2000 + i).padStart(4, '0')}`, -1, {
      bucket: i % 2 === 0 ? 'at_depot' : 'out_for_delivery',
      delivered: false,
    });
  }
  for (let i = 0; i < 55; i++) {
    pushCollection(`7005020${String(1000 + i).padStart(4, '0')}`, -1, { collected: true });
  }
  pushCollection('70050202100', -1, { collected: false });

  // Rest of last 7 days — vary completion (include today-7 for Last 7 Days chip)
  for (let d = -2; d >= -7; d--) {
    const deliveredN = 140 + ((-d) * 3);
    const openN = 4 + ((-d) % 3);
    for (let i = 0; i < deliveredN; i++) {
      pushDelivery(`60${String(10 + (-d)).padStart(2, '0')}30${String(1000 + i).padStart(4, '0')}`, d, {
        bucket: 'delivered',
        delivered: true,
      });
    }
    for (let i = 0; i < openN; i++) {
      pushDelivery(`60${String(10 + (-d)).padStart(2, '0')}30${String(9000 + i).padStart(4, '0')}`, d, {
        bucket: 'at_depot',
        delivered: false,
        atRisk: d === 0,
      });
    }
    for (let i = 0; i < 50; i++) {
      pushCollection(`70${String(10 + (-d)).padStart(2, '0')}30${String(1000 + i).padStart(4, '0')}`, d, {
        collected: i < 48,
      });
    }
  }

  // Days −8…−29 so 14d / 30d chart lookbacks have demo bars (lighter volume).
  for (let d = -8; d >= -29; d--) {
    const deliveredN = 40 + ((-d) % 12);
    const openN = 2 + ((-d) % 3);
    for (let i = 0; i < deliveredN; i++) {
      pushDelivery(`60${String(40 + (-d)).padStart(2, '0')}40${String(1000 + i).padStart(4, '0')}`, d, {
        bucket: 'delivered',
        delivered: true,
        lifts: 1 + ((i + (-d)) % 5),
      });
    }
    for (let i = 0; i < openN; i++) {
      pushDelivery(`60${String(40 + (-d)).padStart(2, '0')}40${String(9000 + i).padStart(4, '0')}`, d, {
        bucket: 'at_depot',
        delivered: false,
        lifts: 2,
      });
    }
    const collectedN = 20 + ((-d) % 8);
    for (let i = 0; i < collectedN + 1; i++) {
      pushCollection(`70${String(40 + (-d)).padStart(2, '0')}40${String(1000 + i).padStart(4, '0')}`, d, {
        collected: i < collectedN,
        lifts: 1 + (i % 3),
      });
    }
  }

  return rows;
}

export function summariseDemoOrdersDaily(
  startDate: string,
  endDate: string,
  allRows: readonly OrdersDailyRow[],
): OrdersDailySummary {
  const rows = allRows.filter((r) => r.grain_date >= startDate && r.grain_date <= endDate);
  const collections = rows.filter((r) => r.direction === 'collection');
  const deliveries = rows.filter((r) => r.direction === 'delivery');
  const partial = deliveries.filter((r) => r.partial_delivery);
  const delivery_buckets = {
    delivered: bucketMetric(deliveries, 'delivered'),
    partial: bucketMetric(deliveries, 'partial'),
    out_for_delivery: bucketMetric(deliveries, 'out_for_delivery'),
    at_depot: bucketMetric(deliveries, 'at_depot'),
    in_transit: bucketMetric(deliveries, 'in_transit'),
    removed_from_plan: bucketMetric(deliveries, 'removed_from_plan'),
    exception: bucketMetric(deliveries, 'exception'),
    unmapped: bucketMetric(deliveries, 'unmapped'),
  };
  const collection_buckets = {
    delivered: bucketMetric(collections, 'delivered'),
    partial: bucketMetric(collections, 'partial'),
    out_for_delivery: bucketMetric(collections, 'out_for_delivery'),
    at_depot: bucketMetric(collections, 'at_depot'),
    in_transit: bucketMetric(collections, 'in_transit'),
    removed_from_plan: bucketMetric(collections, 'removed_from_plan'),
    exception: bucketMetric(collections, 'exception'),
    unmapped: bucketMetric(collections, 'unmapped'),
  };
  const stageMetric = (stage: CollectionStage) =>
    measure(collections.filter((r) => r.collection_stage === stage));
  const collection_stages = {
    awaiting_collection: stageMetric('awaiting_collection'),
    collected: stageMetric('collected'),
    at_collection_depot: stageMetric('at_collection_depot'),
    dispatched_onward: stageMetric('dispatched_onward'),
  };
  const missed = rows.filter((r) => r.missed_am);
  const atRisk = rows.filter((r) => r.at_risk_1700);
  const booked = rows.filter((r) => r.booked_in_excluded);
  const dueBucketMetric = (bucket: 'overdue' | 'due_today' | 'due_future') =>
    measure(deliveries.filter((r) => r.due_bucket === bucket));

  return {
    organisation_id: 'demo',
    start_date: startDate,
    end_date: endDate,
    row_count: rows.length,
    collections: progress(collections, (r) => r.collected),
    deliveries: progress(
      deliveries,
      (r) => r.delivered,
      (r) => Boolean(r.partial_delivery),
    ),
    deliveries_by_due_bucket: {
      overdue: dueBucketMetric('overdue'),
      due_today: dueBucketMetric('due_today'),
      due_future: dueBucketMetric('due_future'),
    },
    // Demo data never sets includeBacklog — no unscheduled legs to report.
    unscheduled: {
      deliveries: { consignments: 0, lifts: 0 },
      collections: { consignments: 0, lifts: 0 },
    },
    overdue: { deliveries: { consignments: 0, lifts: 0 }, collections: { consignments: 0, lifts: 0 } },
    stale: { deliveries: { consignments: 0, lifts: 0 }, collections: { consignments: 0, lifts: 0 }, after_days: 7, loaded: true },
    delivery_buckets,
    collection_buckets,
    collection_stages,
    collections_without_poc: { consignments: 0, lifts: 0 },
    collections_not_received: { consignments: 0, lifts: 0 },
    collection_conditions: {
      on_track: measure(collections),
      at_risk: { consignments: 0, lifts: 0 },
      overdue: { consignments: 0, lifts: 0 },
      needs_investigation: { consignments: 0, lifts: 0 },
      expected_exception: { consignments: 0, lifts: 0 },
    },
    collection_sources: {
      own_customer: measure(collections.filter((r) => r.collection_source === 'own_customer')),
      network: measure(collections.filter((r) => r.collection_source === 'network')),
      unknown: { consignments: 0, lifts: 0 },
    },
    unmapped_status: delivery_buckets.unmapped,
    partial_deliveries: {
      consignments: new Set(partial.map((r) => r.tracking_id)).size,
      lifts_delivered: partial.reduce((sum, r) => sum + (r.lifts_delivered ?? 0), 0),
      lifts_outstanding: partial.reduce((sum, r) => sum + (r.lifts_outstanding ?? 0), 0),
    },
    service_risk: {
      missed_am: {
        consignments: missed.length,
        lifts: measure(missed).lifts,
        impact_gbp: missed.reduce((s, r) => s + (r.impact_gbp ?? 0), 0) || null,
      },
      at_risk_1700: {
        consignments: atRisk.length,
        lifts: measure(atRisk).lifts,
        impact_gbp_min: atRisk.length ? 20 : null,
        impact_gbp_max: atRisk.length ? 80 : null,
      },
      booked_in_excluded: {
        consignments: booked.length,
        lifts: measure(booked).lifts,
      },
      missed_am_codes: ['AM'],
      at_risk_service_group: 'timed',
      at_risk_due_time: '17:00',
    },
    eta: summariseEtaFromDeliveries(deliveries),
    data_quality: {
      collection_date_semantics: 'provisional',
      collection_date_source: 'date_entered',
      delivery_date_semantics: 'confirmed',
      delivery_date_source: 'due_date',
    },
  };
}

export function buildDemoOrdersDailyBundle(
  startDate: string,
  endDate: string,
  asOf: Date = new Date(),
): { records: OrdersDailyRow[]; summary: OrdersDailySummary } {
  const all = buildDemoOrdersDailyRows(asOf);
  const records = all.filter((r) => r.grain_date >= startDate && r.grain_date <= endDate);
  return {
    records,
    summary: summariseDemoOrdersDaily(startDate, endDate, all),
  };
}
